import React, { useState, useEffect, useRef } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { auth, app, API_BASE_URL } from '../../firebase/firebaseConfig';
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Package, LogOut } from "lucide-react";
import ErrorModal from '../ui/ErrorModal';
import { ALLOWED_SHIPPING_COUNTRIES, getCountryByCode, validatePostcode, getPostcodeLabel } from '../../utils/shippingCountries';

function ShippingAddressConfirmation({ email, numOfFamilies, shippingAddressAtOrder, shippingCountryAtOrder, onComplete }) {
  const [shipAllTogether, setShipAllTogether] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [manualEntry, setManualEntry] = useState({});
  const [addressSelected, setAddressSelected] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const autocompleteRefs = useRef({});
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsGoogleLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script is already loading, wait for it
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.places) {
          setIsGoogleLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    // Load the script for the first time
    console.log('Loading Google Maps script...');
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.onload = () => {
      console.log('Google Maps script loaded successfully');
      console.log('Google Maps available:', !!window.google);

      // With loading=async, we need to wait for the Places library to initialize
      const checkPlacesLoaded = setInterval(() => {
        console.log('Checking if Places API is available:', !!window.google?.maps?.places);
        if (window.google?.maps?.places) {
          console.log('Places API available: true');
          setIsGoogleLoaded(true);
          clearInterval(checkPlacesLoaded);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkPlacesLoaded);
        if (!window.google?.maps?.places) {
          console.error('Places API failed to load after 10 seconds');
        }
      }, 10000);
    };
    script.onerror = (error) => {
      console.error('Failed to load Google Maps script:', error);
    };
    document.head.appendChild(script);
  }, []);

  // Initialize addresses array with pre-populated data or empty
  useEffect(() => {
    console.log('ShippingAddressConfirmation - Props received:', {
      numOfFamilies,
      shippingAddressAtOrder,
      shippingCountryAtOrder
    });

    const initialAddresses = [];
    for (let i = 0; i < numOfFamilies; i++) {
      if (i === 0 && shippingAddressAtOrder) {
        // Pre-populate primary address from order data
        const addressData = {
          name: shippingAddressAtOrder.name || '',
          line1: shippingAddressAtOrder.line1 || '',
          line2: shippingAddressAtOrder.line2 || '',
          city: shippingAddressAtOrder.city || '',
          state: shippingAddressAtOrder.state || '',
          postalCode: shippingAddressAtOrder.postalCode || '',
          country: shippingAddressAtOrder.country || shippingCountryAtOrder || 'GB',
          hearthIndex: i,
        };
        console.log('Pre-populating primary address with:', addressData);
        initialAddresses.push(addressData);
        // If pre-populated, mark address as selected
        if (shippingAddressAtOrder.line1 || shippingAddressAtOrder.name) {
          console.log('Marking address as selected because line1 or name exists');
          setAddressSelected(prev => ({ ...prev, [i]: true }));
        }
      } else {
        initialAddresses.push({
          name: '',
          line1: '',
          line2: '',
          city: '',
          state: '',
          postalCode: '',
          country: shippingCountryAtOrder || 'GB',
          hearthIndex: i,
        });
      }
    }
    console.log('Final initialAddresses:', initialAddresses);
    setAddresses(initialAddresses);
  }, [numOfFamilies, shippingAddressAtOrder, shippingCountryAtOrder]);

  // Initialize autocomplete for each address
  useEffect(() => {
    if (!isGoogleLoaded || !window.google?.maps?.places) {
      console.log('Google Maps not loaded yet');
      return;
    }

    // Small delay to ensure DOM elements exist after render
    const timer = setTimeout(() => {
      addresses.forEach((address, index) => {
        const inputId = `autocomplete-input-${index}`;
        const inputElement = document.getElementById(inputId);

        if (inputElement && !autocompleteRefs.current[index] && !manualEntry[index]) {
          try {
            console.log(`Initializing autocomplete for address ${index}, country: ${address.country}`);
            const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
              componentRestrictions: { country: address.country.toLowerCase() },
              fields: ['address_components'],
              types: ['address'],
            });

            autocomplete.addListener('place_changed', () => {
              const place = autocomplete.getPlace();
              console.log('Place selected:', place);
              if (place.address_components) {
                handlePlaceSelected(index, place.address_components);
              }
            });

            autocompleteRefs.current[index] = autocomplete;
            console.log(`Autocomplete initialized for address ${index}`);
          } catch (error) {
            console.error(`Error initializing autocomplete for address ${index}:`, error);
          }
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [isGoogleLoaded, addresses.length, manualEntry, shipAllTogether]);

  // Update autocomplete country restrictions when country changes
  useEffect(() => {
    addresses.forEach((address, index) => {
      const autocomplete = autocompleteRefs.current[index];
      if (autocomplete && address.country) {
        autocomplete.setComponentRestrictions({ country: address.country.toLowerCase() });
      }
    });
  }, [addresses]);

  const handlePlaceSelected = (index, addressComponents) => {
    // Log all components to see what we're getting
    console.log('Address components received:', addressComponents);
    addressComponents.forEach(component => {
      console.log(`${component.types.join(', ')}: ${component.long_name} (short: ${component.short_name})`);
    });

    const getComponent = (types, format = 'long_name') => {
      // Accept either a single type or array of types
      const typeArray = Array.isArray(types) ? types : [types];

      for (const type of typeArray) {
        const component = addressComponents.find(c => c.types.includes(type));
        if (component) {
          return component[format];
        }
      }
      return '';
    };

    // Build street address (line1) - try multiple strategies
    let line1 = '';
    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const premise = getComponent('premise'); // For building names
    const subpremise = getComponent('subpremise'); // For unit/apt numbers

    // Strategy 1: Standard street number + route
    if (streetNumber && route) {
      line1 = `${streetNumber} ${route}`;
    } else if (route) {
      // Strategy 2: Just the route (street name without number)
      line1 = route;
    } else if (premise) {
      // Strategy 3: Premise/building name
      line1 = premise;
    }

    line1 = line1.trim();

    // Get the country to determine city logic
    const country = addresses[index]?.country || 'GB';

    // City - different logic for UK vs other countries
    let city = '';
    if (country === 'GB') {
      // For UK: prefer postal_town over locality (postal_town is the official city)
      city = getComponent(['postal_town', 'locality', 'administrative_area_level_2']);
    } else {
      // For other countries: prefer locality
      city = getComponent(['locality', 'postal_town', 'administrative_area_level_2', 'administrative_area_level_3']);
    }

    // State/County - use long_name for better readability
    const state = getComponent('administrative_area_level_1', 'long_name');

    // Postal code
    const postalCode = getComponent('postal_code');

    console.log('Parsed address:', { line1, city, state, postalCode });

    setAddresses(prev => {
      const newAddresses = [...prev];
      newAddresses[index] = {
        ...newAddresses[index],
        line1: line1,
        city: city,
        state: state,
        postalCode: postalCode,
      };
      return newAddresses;
    });

    // Mark address as selected
    setAddressSelected(prev => ({ ...prev, [index]: true }));
  };

  const handleAddressFieldChange = (index, field, value) => {
    setAddresses(prev => {
      const newAddresses = [...prev];
      newAddresses[index] = {
        ...newAddresses[index],
        [field]: value,
      };
      return newAddresses;
    });
  };

  const handleCountryChange = (index, countryCode) => {
    setAddresses(prev => {
      const newAddresses = [...prev];
      newAddresses[index] = {
        ...newAddresses[index],
        country: countryCode,
      };
      return newAddresses;
    });
  };

  const handleManualEntry = (index) => {
    setManualEntry(prev => ({ ...prev, [index]: true }));
    setAddressSelected(prev => ({ ...prev, [index]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Determine which addresses to submit
      let addressesToSubmit;
      if (shipAllTogether) {
        // Duplicate the first address for all hearths
        addressesToSubmit = addresses.map((_, i) => ({
          ...addresses[0],
          hearthIndex: i,
        }));
      } else {
        // Use individual addresses
        addressesToSubmit = addresses;
      }

      // Validate all addresses
      for (const address of addressesToSubmit) {
        if (!address.name || !address.line1 || !address.city || !address.postalCode || !address.country) {
          setErrorMessage('All address fields (name, country, address, city, postcode) are required.');
          setShowErrorModal(true);
          setIsLoading(false);
          return;
        }

        // Validate postcode format
        if (!validatePostcode(address.postalCode, address.country)) {
          const country = getCountryByCode(address.country);
          setErrorMessage(`Invalid ${country?.postcodeLabel || 'postcode'} format for ${address.name || `Hearth ${address.hearthIndex + 1}`}.`);
          setShowErrorModal(true);
          setIsLoading(false);
          return;
        }
      }

      // Submit to backend
      const currentUser = auth.currentUser;
      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${API_BASE_URL}/user/confirmShippingAddresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          email,
          shippingAddresses: addressesToSubmit,
        }),
      });

      const data = await response.json();
      if (data.success) {
        onComplete();
      } else {
        setErrorMessage(data.error || 'Failed to save shipping addresses. Please try again.');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    const authInstance = getAuth(app);
    try {
      await signOut(authInstance);
      window.location.reload();
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  if (!isGoogleLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const renderAddressFields = (index) => {
    const address = addresses[index];
    const showAdditionalFields = addressSelected[index] || manualEntry[index];

    // Determine if we should show "County" or "State" based on country
    const isUS = address.country === 'US';
    const stateLabel = isUS ? 'State' : 'County';

    return (
      <div className="space-y-3 p-3 border border-gray-200 rounded-lg bg-white">
        <h3 className="font-medium flex items-center gap-2 mb-1">
          <Package className="h-4 w-4" />
          {index === 0 ? <>Primary Hearth <span className="text-sm font-normal text-gray-500">(Your Home)</span></> : `Additional Hearth #${index}`}
        </h3>

        {/* Name Field */}
        <Input
          id={`name-${index}`}
          type="text"
          placeholder="Full Name"
          value={address.name}
          onChange={(e) => handleAddressFieldChange(index, 'name', e.target.value)}
          required
        />

        {/* Country Selection */}
        <div>
          <Select
            value={address.country}
            onValueChange={(value) => handleCountryChange(index, value)}
          >
            <SelectTrigger id={`country-${index}`}>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {ALLOWED_SHIPPING_COUNTRIES.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">
            Currently shipping to UK, EU, and US addresses only
          </p>
        </div>

        {/* Address Line 1 with Autocomplete */}
        {!manualEntry[index] ? (
          <>
            <Input
              id={`autocomplete-input-${index}`}
              type="text"
              placeholder="Address"
              value={address.line1}
              onChange={(e) => handleAddressFieldChange(index, 'line1', e.target.value)}
              required
            />
            {!addressSelected[index] && (
              <button
                type="button"
                onClick={() => handleManualEntry(index)}
                className="text-sm text-gray-600 hover:underline"
              >
                Enter address manually
              </button>
            )}
          </>
        ) : (
          <Input
            id={`manual-input-${index}`}
            type="text"
            placeholder="Address"
            value={address.line1}
            onChange={(e) => handleAddressFieldChange(index, 'line1', e.target.value)}
            required
          />
        )}

        {/* Show additional fields after autocomplete selection or manual entry */}
        {showAdditionalFields && (
          <>
            <Input
              id={`line2-${index}`}
              type="text"
              placeholder="Address Line 2"
              value={address.line2}
              onChange={(e) => handleAddressFieldChange(index, 'line2', e.target.value)}
            />

            <div className="grid grid-cols-2 gap-3">
              <Input
                id={`city-${index}`}
                type="text"
                placeholder="City"
                value={address.city}
                onChange={(e) => handleAddressFieldChange(index, 'city', e.target.value)}
                required
              />

              <Input
                id={`postalCode-${index}`}
                type="text"
                placeholder={getPostcodeLabel(address.country)}
                value={address.postalCode}
                onChange={(e) => handleAddressFieldChange(index, 'postalCode', e.target.value)}
                required
              />
            </div>

            <Input
              id={`state-${index}`}
              type="text"
              placeholder={stateLabel}
              value={address.state}
              onChange={(e) => handleAddressFieldChange(index, 'state', e.target.value)}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Sign out link */}
        <div className="flex justify-end">
          <button
            onClick={handleSignOut}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <LogOut className="h-3 w-3" />
            Sign out
          </button>
        </div>

        <Card>
          <CardHeader className="text-center p-3">
            <div className="flex justify-center mb-3">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Package className="h-6 w-6 text-gray-700" />
              </div>
            </div>
            <CardTitle className="text-2xl">Confirm Shipping Addresses</CardTitle>
            <CardDescription className="text-base">
              You're receiving {numOfFamilies} Hearth{numOfFamilies > 1 ? 's' : ''}. Please confirm where they should be shipped.
            </CardDescription>
          </CardHeader>

          <CardContent className="p-3 pt-0">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Primary Address */}
              {renderAddressFields(0)}

              {/* Ship All Together Checkbox (only show if multiple hearths) */}
              {numOfFamilies > 1 && (
                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <Checkbox
                    id="shipAllTogether"
                    checked={shipAllTogether}
                    onCheckedChange={setShipAllTogether}
                  />
                  <label
                    htmlFor="shipAllTogether"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Ship all other Hearths to the same address
                  </label>
                </div>
              )}

              {/* Additional Addresses (if not shipping together) */}
              {numOfFamilies > 1 && !shipAllTogether && (
                <div className="space-y-4">
                  {Array.from({ length: numOfFamilies - 1 }, (_, i) => i + 1).map(index => (
                    <div key={index}>
                      {renderAddressFields(index)}
                    </div>
                  ))}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-gray-800 text-white hover:bg-gray-700"
                disabled={isLoading}
              >
                {isLoading ? 'Confirming...' : 'Confirm Shipping Addresses'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Error Modal */}
        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          title="Shipping Address Error"
          onClose={() => {
            setShowErrorModal(false);
            setErrorMessage('');
          }}
        />
      </div>
    </div>
  );
}

export default ShippingAddressConfirmation;
